<template>
  <div class="workspacefolders-list">
    <!-- ALL FOLDERS -->
    <sortable-list
      :items="workspaceCtx.currentWorkspaceFolders"
      line-class="line workspacefolders-list__line"
      order-by="order"
      @reorder-item="onFolderReorder($event)"
    >
      <template #default="{ item: folder }">
        <router-link
          v-slot="{ href, navigate, isActive }"
          :to="`${workspaceBaseUrl}/${folder.slug}`"
          custom
        >
          <div
            class="workspacefolders-nav__entry workspacefolders-nav__entry-block"
            :class="{
              'is-active': isActive,
              '-drag-target': folderDraggedOver == folder.id
            }"
            @contextmenu.prevent="openFolderContextMenu($event, folder)"
            @dragover="onFolderDragOver($event, folder)"
            @dragleave="folderDraggedOver = undefined"
            @drop="onFolderDrop($event, folder)"
            @dblclick="startRenamingFolder(folder)"
          >
            <a
              :href="href"
              draggable="false"
              class="workspacefolders-nav__link"
              @click="navigate"
            >
              {{ folder.name }}
            </a>
            <button
              class="workspacefolders-nav__entry-button"
              type="button"
              @click="openFolderContextMenu($event, folder)"
            >
              <svg width="30" height="20" viewBox="0 0 30 20">
                <circle cx="15" cy="10" r="3" fill="currentColor" />
                <circle cx="6" cy="10" r="3" fill="currentColor" />
                <circle cx="24" cy="10" r="3" fill="currentColor" />
              </svg>
            </button>
          </div>
        </router-link>
        <input
          v-if="renaming.folderToRename?.id == folder.id"
          ref="folderRenameInput"
          v-model="renaming.newFolderName"
          :aria-label="$t('labels.renamedFolderName')"
          class="workspacefolders-list__input"
          @blur="renameFolder()"
          @keyup.enter="folderRenameInput?.blur()"
        />
      </template>
    </sortable-list>

    <!-- FOLDER CONTEXT MENU -->
    <context-menu
      v-if="contextmenu.folder"
      :x="contextmenu.x"
      :y="contextmenu.y"
      :non-closing-elements="
        contextmenu.openingButton ? [contextmenu.openingButton] : []
      "
      @close="closeFolderContextMenu()"
    >
      <button
        class="context-menu__entry"
        type="button"
        @click="moving.folder = contextmenu.folder"
      >
        {{ $t("actions.moveFolder") }}
      </button>
      <button
        class="context-menu__entry"
        type="button"
        @click="copying.folder = contextmenu.folder"
      >
        {{ $t("actions.copyFolder") }}
      </button>
      <button
        class="context-menu__entry"
        type="button"
        @click="tryDestroyingFolder(contextmenu.folder)"
      >
        {{ $t("actions.delete") }}
      </button>
      <button
        class="context-menu__entry"
        type="button"
        @click="startRenamingFolder(contextmenu.folder)"
      >
        {{ $t("actions.rename") }}
      </button>
    </context-menu>

    <!-- CREATE FOLDER -->
    <div class="workspacefolders-list__createinput">
      <input-text
        v-model:value="creating.folderName"
        :placeholder="`+ ${$t('labels.newFolder')}`"
        :icons="['plus']"
        @click:plus="tryCreatingFolder()"
        @enter="tryCreatingFolder()"
      />
    </div>

    <folder-duplicate-popup
      :visible="copying.folder != null"
      :folder="copying.folder"
      @close="copying.folder = undefined"
    />

    <folder-move-popup
      :visible="moving.folder != null"
      :folder="moving.folder"
      @close="moving.folder = undefined"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * This is a list of all folders in a workspace.
 */

import { reactive, ref, nextTick, computed } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import SortableList from "@core/ui/SortableList.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import InputText from "@core/input/InputText.vue";

import FolderDuplicatePopup from "@manager/workspaces/FolderDuplicatePopup.vue";
import FolderMovePopup from "./FolderMovePopup.vue";

import { showConfirm, showError } from "@core/ui/alert/alert";
import { useWorkspaces } from "@/api/workspaces.store";
import { useModels } from "@/api/models.store";
import { getRouteToFolder } from "@core/routes/routeDetector";

import {
  NoInternetError,
  type BadRequestError,
  type GenericError
} from "@core/errors/Errors";
import { useToast } from "vue-toastification";

interface Props {
  organizationSlug?: string;
  teamSlug?: string;
  folderSlug?: string;
}
const props = withDefaults(defineProps<Props>(), {
  organizationSlug: undefined,
  teamSlug: undefined,
  folderSlug: undefined
});

const folderRenameInput = ref<HTMLInputElement>();

const router = useRouter();
const toast = useToast();

const workspaces = useWorkspaces(props);
const workspaceCtx = reactive(workspaces);

const { t } = useI18n();
const { createFolder, destroyFolder, updateFolder, reorderFolder } = workspaces;
const { getFolderModels, getModelsFilteredByFolder } = useModels();

const workspaceBaseUrl = computed(() => {
  if (!workspaceCtx.currentWorkspace) return;

  if (workspaceCtx.currentWorkspace.isPersonal) {
    return "/models/my";
  } else {
    return `/models/${workspaceCtx.currentWorkspace.organizationSlug}/${workspaceCtx.currentWorkspace.slug}`;
  }
});

// =================
// COPYING

const copying = reactive({
  folder: undefined as
    | undefined
    | { id: number; slug: string; name: string; workspace: number }
});

// =================
// MOVING

const moving = reactive({
  folder: undefined as
    | undefined
    | { id: number; slug: string; name: string; workspace: number }
});

// =================
// FOLDER CREATION

const creating = reactive({
  inProcess: false,
  folderName: ""
});

async function tryCreatingFolder() {
  if (!workspaceCtx.currentWorkspace) return;

  const name = creating.folderName;

  try {
    const newFolder = await createFolder({
      workspace: workspaceCtx.currentWorkspace.id,
      name: name
    });

    creating.inProcess = false;
    creating.folderName = "";

    // move to new folder
    const folderRoute = getRouteToFolder(newFolder);
    router.push(folderRoute);
  } catch (_error) {
    const error = _error as GenericError | BadRequestError;
    const errorMessage = "fields" in error ? error?.fields?.name : undefined;
    if (errorMessage) {
      showError(errorMessage);
    } else if (error.status === 400) {
      // Name conflict
      showError(t("errors.uniqueFolderName"));
    } else {
      console.log(error);
      showError(error);
    }
  }
}

async function tryDestroyingFolder(folder: {
  id: number;
  slug: string;
  name: string;
}) {
  if (!workspaceCtx.currentWorkspace) return;

  let folderModels = getModelsFilteredByFolder(folder.id);

  // if there are NO cached models in that folder,
  // make a network request to be sure that there are REALLY
  // no models in that folder before deleting it.
  if (folderModels.length == 0) {
    folderModels = await getFolderModels(
      workspaceCtx.currentWorkspace,
      folder.slug
    );
  }

  if (folderModels.length > 0) {
    const confirmedDeletion = await showConfirm(
      t("questions.confirmDeleteFolder", {
        name: folder.name,
        modelCount: folderModels.length
      })
    );
    if (confirmedDeletion == false) return;
  }

  const folderIndex = workspaceCtx.currentWorkspaceFolders.findIndex(
    (f) => f.id === folder.id
  );

  try {
    await destroyFolder({
      workspace: workspaceCtx.currentWorkspace.id,
      ...folder
    });

    // redirect to main view if the folder was deleted
    if (props.folderSlug === folder.slug) {
      if (workspaceCtx.currentWorkspaceFolders.length == 0) {
        router.push({
          path: `${workspaceBaseUrl.value}`
        });
      } else {
        let nextIndex = folderIndex - 1;
        if (nextIndex < 0) nextIndex = folderIndex;
        const nextFolder = workspaceCtx.currentWorkspaceFolders[nextIndex];
        router.push(getRouteToFolder(nextFolder));
      }
    }
  } catch (error) {
    showError(error as Error);
  }
}

// =================
// RENAMING

const renaming = reactive({
  folderToRename: null as null | { name: string; id: number },
  newFolderName: ""
});

function startRenamingFolder(folder: { name: string; id: number }) {
  renaming.folderToRename = folder;
  renaming.newFolderName = folder.name;
  nextTick(() => {
    folderRenameInput.value?.focus();
    folderRenameInput.value?.select();
  });
}

async function renameFolder() {
  if (!workspaceCtx.currentWorkspace) return;
  if (!renaming.folderToRename) return;

  const newName = renaming.newFolderName.trim();
  if (!newName) {
    renaming.folderToRename = null;
    return;
  }

  if (newName === renaming.folderToRename.name) {
    // No change, just close the input
    renaming.folderToRename = null;
    return;
  }

  const redirectAfter =
    workspaceCtx.currentFolder?.id == renaming.folderToRename.id;
  try {
    const updatedFolder = await updateFolder({
      id: renaming.folderToRename.id,
      workspace: workspaceCtx.currentWorkspace.id,
      name: renaming.newFolderName
    });
    if (redirectAfter) {
      const newFolderRoute = getRouteToFolder(updatedFolder);
      router.replace(newFolderRoute);
    }
  } catch (_error) {
    const error = _error as GenericError | BadRequestError;
    const errorMessage = "fields" in error ? error?.fields?.name : undefined;
    if (errorMessage) {
      showError(errorMessage);
    } else if (error.status === 400) {
      // Name conflict
      showError(t("errors.uniqueFolderName"));
    } else {
      console.log(error);
      showError(error);
    }
  }

  renaming.folderToRename = null;
}

// =================
// FOLDER REORDERING

async function onFolderReorder(itemOrderEvent: { id: number; order: number }) {
  const id = itemOrderEvent.id;
  const order = itemOrderEvent.order;

  const folder = workspaceCtx.currentWorkspaceFolders.find((f) => f.id == id);

  if (!folder) {
    console.error("Folder not found");
    return;
  }

  try {
    await reorderFolder({
      ...folder,
      order
    });
  } catch (_error) {
    if (_error instanceof NoInternetError) {
      toast.error(_error.message);
    } else if (_error instanceof Error) {
      showError(_error);
    }
  }
}

// =================
// FOLDER DRAG & DROP

const folderDraggedOver = ref<number>();

function onFolderDragOver(event: DragEvent, folder: { id: number }) {
  if (
    event.dataTransfer &&
    event.dataTransfer.types.includes("simcision/modelid")
  ) {
    event.preventDefault();
    folderDraggedOver.value = folder.id;
    event.dataTransfer.dropEffect = "move";
  }
}

async function onFolderDrop(
  event: DragEvent,
  folder: { id: number; slug: string }
) {
  event.preventDefault();

  if (!workspaceCtx.currentWorkspace) return;

  const currentFolder = workspaceCtx.currentFolder;
  if (!currentFolder) return;

  const modelId = event.dataTransfer?.getData("simcision/modelid");
  if (!modelId) return;

  if (folder.id == currentFolder.id) return;

  // MOVE THE MODEL
  await useModels().moveModel({
    id: toIntIfNotUuid(modelId),
    workspaceId: workspaceCtx.currentWorkspace.id,
    folderId: folder.id,
    folderSlug: folder.slug
  });
  folderDraggedOver.value = undefined;
}

/**
 * Makes sure not to transform uuids
 * @returns The number as int or the uuid
 */
function toIntIfNotUuid(str: string | number) {
  if (typeof str != "string") return str;
  if (str.includes("-")) {
    return str;
  }
  return parseInt(str);
}

// =================
// CONTEXTMENU

const contextmenu = reactive({
  folder: null as null | {
    id: number;
    slug: string;
    name: string;
    workspace: number;
  },
  x: 0,
  y: 0,
  openingButton: null as null | HTMLElement
});

function openFolderContextMenu(
  event: MouseEvent,
  folder: { id: number; slug: string; name: string; workspace: number }
) {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
    ".workspacefolders-nav__entry-button"
  );
  if (button && button == contextmenu.openingButton) {
    contextmenu.folder = null;
    contextmenu.openingButton = null;
    return;
  }
  contextmenu.folder = folder;
  contextmenu.openingButton = button || null;
  if (button) {
    const rect = button.getBoundingClientRect();
    contextmenu.x = rect.left;
    contextmenu.y = rect.bottom + 3;
  } else {
    contextmenu.x = event.clientX + 3;
    contextmenu.y = event.clientY + 3;
  }
}

function closeFolderContextMenu() {
  contextmenu.folder = null;
  contextmenu.openingButton = null;
}
</script>

<style lang="scss">
.workspacefolders-list {
  display: block;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 2rem;

  &__line {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 1fr;
    position: relative;
    min-height: 2rem;
  }

  &__input {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    border: none;
    border-left: 5px solid transparent;
    padding: 0.5rem 1rem;
    font-weight: 600;
    font-family: var(--font-family-ui);
    font-size: 14px;
    &:focus {
      outline: none;
      border-left-color: var(--color-action);
      background-color: var(--color-background);
    }
  }

  &__createinput {
    margin-top: 1rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>
